import { useCallback, useEffect } from 'react';
import { mobileWidthBreakpoint, useItemListStyles } from './ItemList.styles';
import useElementSize from '../../../../hooks/useElementSize';
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    AccordionProps,
    mergeClasses,
    Tab,
    TabList,
    TabListProps,
    Text,
} from '@fluentui/react-components';

export type TabId = string | number;

export interface ItemTab {
    id: TabId;
    label: string;
}

export interface ItemListProps {
    readonly tabs: ItemTab[];
    selectedTabId: TabId | null;
    content: React.ReactNode;
    title: string;
    tabLabelPrefix: string;
    icon?: React.ReactNode;
    onTabSelected: (tabId: TabId) => void;
    onIsMobileChange?: (isMobile: boolean) => void;
}

type ChildListProps = Pick<
    ItemListProps,
    'tabs' | 'selectedTabId' | 'content' | 'tabLabelPrefix' | 'onTabSelected'
>;

const ItemTabs: React.FC<ChildListProps> = ({
    tabs,
    selectedTabId,
    content,
    tabLabelPrefix,
    onTabSelected,
}) => {
    const styles = useItemListStyles();

    const onTabSelect = useCallback<
        Exclude<TabListProps['onTabSelect'], undefined>
    >((_, data) => onTabSelected(data.value as TabId), [onTabSelected]);

    return (
        <div className={styles.tabListRoot}>
            <TabList
                className={styles.tabList}
                selectedValue={selectedTabId}
                vertical
                onTabSelect={onTabSelect}
            >
                {tabs.map((tab) => (
                    <Tab
                        className={styles.tab}
                        value={tab.id}
                        as="button"
                        key={tab.id}
                    >
                        <Text>{tabLabelPrefix} </Text>
                        <Text weight="semibold">{tab.label}</Text>
                    </Tab>
                ))}
            </TabList>
            <div className={styles.tabListItemInfo}>{content}</div>
        </div>
    );
};

const AccordionItems: React.FC<ChildListProps> = ({
    tabs,
    selectedTabId,
    content,
    tabLabelPrefix,
    onTabSelected,
}) => {
    const styles = useItemListStyles();

    const onTabSelect = useCallback<
        Exclude<AccordionProps['onToggle'], undefined>
    >((_, data) => onTabSelected(data.value as TabId), [onTabSelected]);

    return (
        <Accordion
            collapsible={tabs.length > 1}
            onToggle={onTabSelect}
            openItems={selectedTabId !== undefined ? [selectedTabId] : []}
        >
            {tabs.map((tab) => (
                <AccordionItem key={tab.id} value={tab.id}>
                    <AccordionHeader
                        className={mergeClasses(
                            styles.accordionHeader,
                            tabs.length > 1 ? '' : styles.hideExpandIcon
                        )}
                        expandIconPosition="end"
                    >
                        <Text as="h3" size={300}>
                            {tabLabelPrefix}
                        </Text>
                        <Text
                            className={styles.itemTitle}
                            size={300}
                            weight="medium"
                        >
                            {tab.label}
                        </Text>
                    </AccordionHeader>
                    <AccordionPanel className={styles.accordionPannel}>
                        {content}
                    </AccordionPanel>
                </AccordionItem>
            ))}
        </Accordion>
    );
};

const ItemList: React.FC<ItemListProps> = ({
    tabs,
    selectedTabId,
    content,
    title,
    tabLabelPrefix,
    icon,
    onIsMobileChange,
    onTabSelected,
}) => {
    const styles = useItemListStyles();
    const { ref, width = 0 } = useElementSize<HTMLDivElement>();

    const isMobile = width < mobileWidthBreakpoint;

    useEffect(() => onIsMobileChange?.(isMobile), [isMobile, onIsMobileChange]);

    const childProps: ChildListProps = {
        tabs,
        selectedTabId,
        content,
        tabLabelPrefix,
        onTabSelected,
    };

    return (
        <div className={styles.root} ref={ref}>
            <div className={styles.title}>
                <Text as="h3" size={300} weight="bold">
                    {title}
                </Text>
                {icon}
            </div>
            {!isMobile ? (
                <ItemTabs {...childProps} />
            ) : (
                <AccordionItems {...childProps} />
            )}
        </div>
    );
};

export default ItemList;
